import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ComponentStore } from '@ngrx/component-store';
import { tapResponse } from '@ngrx/operators';
import { TranslateService } from '@ngx-translate/core';
import { Observable, switchMap } from 'rxjs';
import { oeeContext } from '../../../core/interceptors/api-interceptor.interceptor';
import { ELoadStatus } from '../../../state/state.interface';
import {
  HttpUtilitiesService,
  IGenericCrudRequestConstructionParameters,
  IGetManyResponse,
} from '../../../utilities/http-utilities.service';
import { IDropdownSettings } from '../mat-dropdown/scw-mat-dropdown.interface';
import { ILine, IShift, IWorkOrder } from './form-association.interface';

export interface IFormAssociationComponentState {
  shifts: IShift[];
  lines: ILine[];
  workOrders: IWorkOrder[];
  shiftsLoading: ELoadStatus;
  linesLoading: ELoadStatus;
  workOrdersLoading: ELoadStatus;
  totalShift: number;
  totalLine: number;
  totalWorkOrder: number;
}

@Injectable()
export class FormAssociationStore extends ComponentStore<IFormAssociationComponentState> {
  readonly shiftsDropdown$ = this.select((state: IFormAssociationComponentState) =>
    state.shifts.map((s: IShift) => ({ id: s.id, name: s.name })),
  );

  readonly linesDropdown$ = this.select((state: IFormAssociationComponentState) =>
    state.lines.map((l: ILine) => ({ id: l.id, name: l.title })),
  );

  readonly workOrdersDropdown$ = this.select((state: IFormAssociationComponentState) =>
    state.workOrders.map((w: IWorkOrder) => ({ id: w.id, name: w.woNumber })),
  );

  readonly loadShift = this.effect((trigger$: Observable<IGenericCrudRequestConstructionParameters>) =>
    trigger$.pipe(
      switchMap((params) => {
        const httpParams: HttpParams = this.httpUtilities.insertGenericCrudRequestParameters(params);
        this.patchState({ shiftsLoading: ELoadStatus.loading });

        return this.getShift(httpParams).pipe(
          tapResponse(
            (response) =>
              this.patchState({
                shifts: response.data,
                shiftsLoading: ELoadStatus.success,
                totalShift: response.total,
              }),
            // eslint-disable-next-line no-console
            (error) => console.error('Error loading shift data', error),
          ),
        );
      }),
    ),
  );

  readonly loadLine = this.effect((trigger$: Observable<IGenericCrudRequestConstructionParameters>) =>
    trigger$.pipe(
      switchMap((params) => {
        const httpParams: HttpParams = this.httpUtilities.insertGenericCrudRequestParameters(params);
        this.patchState({ linesLoading: ELoadStatus.loading });

        return this.getLine(httpParams).pipe(
          tapResponse(
            (response) =>
              this.patchState({ lines: response.data, linesLoading: ELoadStatus.success, totalLine: response.total }),
            // eslint-disable-next-line no-console
            (error) => console.error('Error loading line data', error),
          ),
        );
      }),
    ),
  );

  readonly loadWorkOrder = this.effect((trigger$: Observable<IGenericCrudRequestConstructionParameters>) =>
    trigger$.pipe(
      switchMap((params) => {
        const httpParams: HttpParams = this.httpUtilities.insertGenericCrudRequestParameters(params);
        this.patchState({ workOrdersLoading: ELoadStatus.loading });

        return this.getWorkOrder(httpParams).pipe(
          tapResponse(
            (response) =>
              this.patchState({
                totalWorkOrder: response.total,
                workOrders: response.data,
                workOrdersLoading: ELoadStatus.success,
              }),
            // eslint-disable-next-line no-console
            (error) => console.error('Error loading work-order data', error),
          ),
        );
      }),
    ),
  );

  constructor(
    private readonly http: HttpClient,
    private readonly httpUtilities: HttpUtilitiesService,
    private readonly translate: TranslateService,
  ) {
    super({
      lines: [],
      linesLoading: ELoadStatus.initial,
      shifts: [],
      shiftsLoading: ELoadStatus.initial,
      totalLine: 0,
      totalShift: 0,
      totalWorkOrder: 0,
      workOrders: [],
      workOrdersLoading: ELoadStatus.initial,
    });
  }

  private getShift(params: HttpParams): Observable<IGetManyResponse<IShift>> {
    return this.http.get<IGetManyResponse<IShift>>('scheduler/shifts', { context: oeeContext, params });
  }

  private getLine(params: HttpParams): Observable<IGetManyResponse<ILine>> {
    return this.http.get<IGetManyResponse<ILine>>('lines', { context: oeeContext, params });
  }

  private getWorkOrder(params: HttpParams): Observable<IGetManyResponse<IWorkOrder>> {
    return this.http.get<IGetManyResponse<IWorkOrder>>('work-orders', { context: oeeContext, params });
  }

  public getDefaultMultiSelectConfig(config: Partial<IDropdownSettings> & { text: string }): IDropdownSettings {
    return {
      enableSearchFilter: config.enableSearchFilter ?? true,
      hasDelayedDefaultValue: config.hasDelayedDefaultValue ?? true,
      ...(config.hint ? { hint: this.translate.instant(`widget.formIo.${config.hint}`) } : {}),
      isClientSide: config.isClientSide ?? false,
      repositionDropdownList: config.repositionDropdownList ?? true,
      singleSelection: config.singleSelection ?? false,
      text: this.translate.instant(`page.widgetContent.addEditDialog.${config.text}`),
    };
  }
}
